/**
 * 我要捐赠 — AP兑捐UO 捐币晋级表单 (legacy parity).
 * Top-right 「捐赠记录」→ /donations/records.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { USER_LEVELS, LEVEL_BY_CODE } from '@uo/shared';
import { ApiError } from '../api/client';
import { apAPI } from '../api/ap';
import { levelUpgradeAPI } from '../api/level-upgrade';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { DonationSummaryCard } from '../components/donations/DonationSummaryCard';

interface RpApOrbPreview {
  target_level: string;
  target_level_name: string;
  donation_orb: string;
  unit_price?: string;
  rp_required: string;
  orb_out?: string;
  rp_balance: string;
  can_execute: boolean;
  upgrade_check?: {
    checks?: Array<{
      level_code: string;
      passed: boolean;
      requirements?: Array<{ name: string; required: string; actual: string; met: boolean }>;
    }>;
  };
}

/** RP转AP 1:1 → 挂买排队（prod）；不自动升级。 */
function RpToApHangPanel({
  txPassword,
  securityAnswer,
  showSecurity,
}: {
  txPassword: string;
  securityAnswer: string;
  showSecurity: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isSub = Boolean((profile as Record<string, unknown> | null)?.master_id);

  const previewQuery = useQuery({
    queryKey: ['level-upgrade', 'rp-to-ap', 'preview'],
    queryFn: () => levelUpgradeAPI.rpToApPreview(),
    enabled: !isSub,
    retry: false,
  });
  const preview = previewQuery.data;

  const execMut = useMutation({
    mutationFn: () =>
      levelUpgradeAPI.rpToApExecute({
        amount: amount.trim(),
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
      }),
    onSuccess: (data) => {
      setError(null);
      const match = data.match as { matched?: boolean; reason?: string } | undefined;
      const matched = Boolean(match?.matched);
      setSuccess(
        matched
          ? t('donate.rpToApMatched', 'RP转AP 成功，挂买已撮合；请到 AP兑捐 ORB 后捐赠。')
          : t('donate.rpToApQueued', 'RP转AP 成功，已进入挂买排队（等待卖单撮合）。'),
      );
      setAmount('');
      void qc.invalidateQueries({ queryKey: ['user'] });
      void qc.invalidateQueries({ queryKey: ['wallet'] });
      void qc.invalidateQueries({ queryKey: ['level-upgrade'] });
      void qc.invalidateQueries({ queryKey: ['ap', 'donate-uo'] });
    },
    onError: (e: unknown) => {
      setSuccess(null);
      setError(e instanceof ApiError ? e.message : t('donate.rpToApFailed', 'RP转AP 失败'));
    },
  });

  if (isSub) return null;

  const disabled =
    !amount.trim() ||
    Number(amount) <= 0 ||
    !txPassword.trim() ||
    (showSecurity && !securityAnswer.trim()) ||
    execMut.isPending;

  return (
    <ThemedPanel className="p-4">
      <div className="mb-3 text-sm font-semibold" style={{ color: C.gold }}>
        {t('donate.rpToApTitle', 'RP转AP（挂买排队）')}
      </div>
      <p className="mb-4 text-xs leading-relaxed" style={{ color: C.muted }}>
        {t(
          'donate.rpToApHint',
          '与正式环境一致：RP 与 AP 1:1，转入后进入挂买单排队；有卖单撮合后进入 AP兑捐 ORB，再手动捐赠销毁。不会自动升级。',
        )}
      </p>

      {preview ? (
        <div className="mb-3 text-xs" style={{ color: C.muted }}>
          {t('donate.balance', '余额')}: RP {Number(preview.rp_balance).toFixed(2)} / AP{' '}
          {Number(preview.ap_balance).toFixed(2)}
        </div>
      ) : null}

      <ThemedField label={t('donate.rpToApAmount', '转换数量（RP=AP）')} required>
        <ThemedInput
          type="number"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError(null);
            setSuccess(null);
          }}
          placeholder={t('donate.rpToApAmountPlaceholder', '请输入正整数')}
          min="1"
          step="1"
        />
      </ThemedField>

      {success ? <ThemedNotice tone="success">{success}</ThemedNotice> : null}
      {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => execMut.mutate()}
        className="mt-4 h-11 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
        style={{ background: C.buttonBg, borderColor: C.buttonBorder, color: C.text }}
      >
        {execMut.isPending ? '...' : t('donate.rpToApSubmit', 'RP转AP')}
      </button>
    </ThemedPanel>
  );
}

/** RP 购买 AP 并捐币升级 — 主账户专用通道（子账户后端拦截）。 */
function RpApUpgradePanel({
  txPassword,
  securityAnswer,
  showSecurity,
}: {
  txPassword: string;
  securityAnswer: string;
  showSecurity: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [targetLevel, setTargetLevel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currentSort = LEVEL_BY_CODE[profile?.level_code ?? '']?.sort ?? -1;
  const candidates = useMemo(
    () =>
      USER_LEVELS.filter(
        (lvl) => Number(lvl.donation_uo) > 0 && lvl.sort > currentSort,
      ),
    [currentSort],
  );

  const isSub = Boolean((profile as Record<string, unknown> | null)?.master_id);

  const previewQuery = useQuery({
    queryKey: ['level-upgrade', 'rp-ap-orb', 'preview', targetLevel],
    queryFn: async () =>
      (await levelUpgradeAPI.rpApOrbPreview(targetLevel)) as unknown as RpApOrbPreview,
    enabled: !!targetLevel,
    retry: false,
  });
  const preview = previewQuery.data;
  const previewErr =
    previewQuery.error instanceof ApiError ? previewQuery.error.message : null;
  const targetCheck = preview?.upgrade_check?.checks?.find(
    (c) => c.level_code === targetLevel,
  );
  const unmetRequirements = targetCheck?.requirements?.filter((r) => !r.met) ?? [];

  const execMut = useMutation({
    mutationFn: () =>
      levelUpgradeAPI.rpApOrbExecute({
        target_level: targetLevel,
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
      }),
    onSuccess: () => {
      setError(null);
      setSuccess(
        t('donate.rpUpgradeSuccess', '升级成功！已用 RP 购买 AP 并完成捐币晋级'),
      );
      void qc.invalidateQueries({ queryKey: ['user'] });
      void qc.invalidateQueries({ queryKey: ['level-upgrade'] });
      void qc.invalidateQueries({ queryKey: ['ap', 'donate-uo'] });
    },
    onError: (e: unknown) => {
      setSuccess(null);
      setError(e instanceof ApiError ? e.message : t('donate.rpUpgradeFailed', '升级失败'));
    },
  });

  if (isSub) return null;

  const disabled =
    !targetLevel ||
    !preview?.can_execute ||
    !txPassword.trim() ||
    (showSecurity && !securityAnswer.trim()) ||
    execMut.isPending;

  return (
    <ThemedPanel className="p-4">
      <div className="mb-3 text-sm font-semibold" style={{ color: C.gold }}>
        {t('donate.rpUpgradeTitle', 'RP 购买 AP 升级通道')}
      </div>
      <p className="mb-4 text-xs leading-relaxed" style={{ color: C.muted }}>
        {t(
          'donate.rpUpgradeHint',
          '只用 RP 完成捐币晋级：RP 按币价兑成 AP → ORB，全部捐赠并销毁，一步到位。交易密码沿用上方输入。',
        )}
      </p>

      <ThemedField label={t('donate.rpUpgradeTarget', '目标等级')}>
        <div className="grid grid-cols-3 gap-2">
          {candidates.map((lvl) => {
            const active = targetLevel === lvl.code;
            return (
              <button
                key={lvl.code}
                type="button"
                onClick={() => {
                  setTargetLevel(lvl.code);
                  setError(null);
                  setSuccess(null);
                }}
                className="h-10 rounded-xl border text-xs font-semibold transition-all active:scale-95"
                style={{
                  borderColor: active ? `${C.gold}77` : C.softBorder,
                  background: C.inputBg,
                  color: active ? C.gold : C.muted,
                }}
              >
                {lvl.code} {lvl.name}
              </button>
            );
          })}
        </div>
      </ThemedField>

      {targetLevel && preview ? (
        <div
          className="mt-4 rounded-xl border px-3 py-3 text-xs"
          style={{ borderColor: C.softBorder, background: C.inputBg }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div style={{ color: C.muted }}>{t('donate.rpRequired', '所需 RP')}</div>
              <div className="mt-1 font-mono text-sm font-semibold" style={{ color: C.gold }}>
                {preview.rp_required}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('donate.balance', '余额')}: {Number(preview.rp_balance).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ color: C.muted }}>
                {t('donate.orbOut', '兑得并捐赠 ORB')}
              </div>
              <div className="mt-1 font-mono text-sm font-semibold" style={{ color: C.gold }}>
                {preview.orb_out ? Number(preview.orb_out).toFixed(4) : '—'}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('donate.unitPrice', '币价')}: {preview.unit_price ?? '—'}
              </div>
            </div>
          </div>
          <div className="mt-2" style={{ color: C.muted }}>
            {t('donate.rpFormulaHint', '需捐 {{orb}} ORB × 币价 {{price}}，RP 按 10 为单位向上取整；兑得的 ORB 全部捐赠销毁。', {
              orb: preview.donation_orb,
              price: preview.unit_price ?? '—',
            })}
          </div>
          {!preview.can_execute ? (
            <div className="mt-2" style={{ color: '#F74E5E' }}>
              {unmetRequirements.length
                ? unmetRequirements
                    .map((r) =>
                      t('donate.rpUpgradeUnmet', '{{name}}：需 {{required}}，当前 {{actual}}', {
                        name: r.name,
                        required: r.required,
                        actual: r.actual,
                      }),
                    )
                    .join('；')
                : targetCheck?.passed
                  ? t('donate.rpUpgradeRpShort', 'RP 余额不足')
                  : t('donate.rpUpgradeNotReady', '晋级条件未达成或 RP 余额不足')}
            </div>
          ) : null}
        </div>
      ) : null}

      {targetLevel && previewErr ? <ThemedNotice tone="error">{previewErr}</ThemedNotice> : null}
      {success ? <ThemedNotice tone="success">{success}</ThemedNotice> : null}
      {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => execMut.mutate()}
        className="mt-4 h-11 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
        style={{ background: C.buttonBg, borderColor: C.buttonBorder, color: C.text }}
      >
        {execMut.isPending
          ? '...'
          : t('donate.rpUpgradeSubmit', '用 RP 购买 AP 并升级 {{level}}', {
              level: targetLevel || '—',
            })}
      </button>
    </ThemedPanel>
  );
}

export function Donations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [amount, setAmount] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: preview } = useQuery({
    queryKey: ['ap', 'donate-uo', 'preview'],
    queryFn: () => apAPI.donatePreview(),
    refetchInterval: 30_000,
  });

  const donateMut = useMutation({
    mutationFn: () =>
      apAPI.donate({
        fp_amount: amount.trim(),
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
      }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setAmount('');
      setTxPassword('');
      setSecurityAnswer('');
      qc.invalidateQueries({ queryKey: ['ap', 'donate-uo'] });
      qc.invalidateQueries({ queryKey: ['user'] });
      qc.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('donate.failed', '捐赠失败'));
    },
  });

  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('common.defaultSecurityQuestion');

  const formDisabled =
    !amount.trim() ||
    Number(amount) <= 0 ||
    !txPassword.trim() ||
    (showSecurity && !securityAnswer.trim()) ||
    donateMut.isPending;

  function handleSubmit() {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError(t('donate.amountError', '请输入有效的捐赠数量'));
      return;
    }
    donateMut.mutate();
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('donate.title', '我要捐赠')}
          onBack={() => navigate(-1)}
          theme={C}
          action={t('donate.records', '捐赠记录')}
          onAction={() => navigate('/donations/records')}
        />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '88px' }}
        >
          <DonationSummaryCard
            summary={preview}
            labels={{
              level: t('donate.donorLevel', '捐赠者级别'),
              amount: t('donate.donatedAmount', '捐赠数量'),
              value: t('donate.donatedValue', '捐赠价值'),
            }}
          />

          {success && <ThemedNotice tone="success">{t('donate.success', '捐赠成功')}</ThemedNotice>}
          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('donate.type', '捐赠类型')}>
                <div
                  className="flex h-10 items-center rounded-lg border px-3 text-sm font-semibold"
                  style={{ borderColor: C.softBorder, background: C.inputBg, color: C.gold }}
                >
                  {preview?.donate_type || 'AP兑捐UO'}
                </div>
              </ThemedField>

              <ThemedField label={t('donate.amountLabel', '捐赠UO总数')} required>
                <ThemedInput
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('donate.amountPlaceholder', '请输入捐赠UO总数')}
                  min="0"
                  step="any"
                />
              </ThemedField>

              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityQuestion}
              />
            </div>
          </ThemedPanel>

          <RpToApHangPanel
            txPassword={txPassword}
            securityAnswer={securityAnswer}
            showSecurity={showSecurity}
          />

          <RpApUpgradePanel
            txPassword={txPassword}
            securityAnswer={securityAnswer}
            showSecurity={showSecurity}
          />
        </div>

        <ThemedBottomActionBar>
          <ThemedSubmitButton disabled={formDisabled} onClick={handleSubmit}>
            {donateMut.isPending ? '...' : t('donate.confirm', '确认捐赠')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export default Donations;
